/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { call, delay, put, race } from "redux-saga/effects";

import * as ApplicationConstants from "../../Constants/ApplicationConstants";
import * as applicationApi from "../../Services/ApplicationApi";

import { IApplicationAssign } from "Interfaces/Application/IApplicationAssign";
import { IApplicationFlagRequest } from "Interfaces/Application/IApplicationFlagRequest";
import { IApplicationGetAssign } from "Interfaces/Application/IApplicationGetAssign";
import { IApplicationGetAssignDataResult } from "Interfaces/Application/IApplicationGetAssignDataResult";
import { IApplicationListDataResult } from "Interfaces/Application/IApplicationListDataResult";
import { IApplicationResponse } from "Interfaces/Application/IApplicationResponse";
import { IApplicationScoreRequest } from "Interfaces/Application/IApplicationScoreRequest";
import { IApplicationsRequest } from "Interfaces/Application/IApplicationsRequest";
import { IFilterValue } from "Interfaces/IFilterValue";

import { HttpStatusCode } from "../../Constants/HttpStatusCode";
import { IApplicationDataResult } from "../../Interfaces/Application/IApplicationDataResult";
import { IApiErrorResult, IApiResult2 } from "../../Interfaces/IApiResult";
import { getDefaultApiTimeOutConfiguration } from "../../Utility/Utils";
import {
  assignApplicationFail,
  assignApplicationSuccess,
  downloadApplicationsToCSVFail,
  downloadApplicationsToCSVSuccess,
  flagApplicationFail,
  flagApplicationSuccess,
  getAssignApplicationFail,
  getAssignApplicationSuccess,
  loadApplicationFail,
  loadApplicationSuccess,
  scoreApplicationFail,
  scoreApplicationSuccess,
  searchApplicationsFail,
  searchApplicationsSuccess,
  IAssignApplication,
  IDownloadApplicationsToCSV,
  IFlagApplication,
  IGetAssignApplication,
  ILoadApplication,
  IScoreApplication,
  ISearchApplications,
  IGetAvailableAssignApplication,
  IBulkAssignApplication,
  getAvailableAssignApplicationFail,
  getAvailableAssignApplicationSuccess,
  bulkAssignApplicationSuccess,
  bulkAssignApplicationFail,
} from "./ApplicationAction";
import { IApplicationBulkAssign } from "Interfaces/Application/IApplicationBulkAssign";
import { IApplicationGetBulkAssignDataResult } from "Interfaces/Application/IApplicationGetBulkAssignDataResult";
import { IApplicationGetBulkAssign } from "Interfaces/Application/IApplicationGetBulkAssign";

/**
 * Updates the request body with filter values.
 * @param {IFilterValue[]} filterValues The list of filters.
 * @return {any} The request to be sent.
 */
const updateFilterValues = (action: IDownloadApplicationsToCSV,): any => {
  const filterValues = action.selectedApplicationsFilterValues;

  const requestBody: any = {
    applicationType: "Any",
    applicationStatusList: [],
    countryRegionList: [],
    stateProvinceList: [],
    technologyAreaList: [],
    technologyAreaGroupList: [],
    assignedToMe: null,
    technologyAreaStatusList: [],
    technologyAreaStatusReasonList: [],
    flagReason: [],
    cohort: undefined,
    technologyAreaScoreList: [],
    reviewStatusList: [],
    annualExceptionList: [],
    availableCpmList: [],
    requestorEmailAddress: action.requestorEmailAddress ?? null,
  };

  filterValues.forEach(function (item) {
    switch (item.fieldName) {
      case ApplicationConstants.APPLICATION_TYPE:
        requestBody.applicationType = item.value;
        break;
      case ApplicationConstants.APPLICATION_STATUS:
        requestBody.applicationStatusList.push(item.value);
        break;
      case ApplicationConstants.APPLICATION_COUNTRY_REGION:
        requestBody.countryRegionList.push(item.value);
        break;
      case ApplicationConstants.APPLICATION_STATE_PROVINCE:
        requestBody.stateProvinceList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_GROUP:
        requestBody.technologyAreaGroupList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA:
        requestBody.technologyAreaList.push(item.value);
        break;
      case ApplicationConstants.ASSIGNED_TO:
        requestBody.assignedToMe = item.value === ApplicationConstants.ANY ? null : (item.value === ApplicationConstants.ME);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_STATUS:
        requestBody.technologyAreaStatusList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_STATUS_REASON:
        requestBody.technologyAreaStatusReasonList.push(item.value);
        break;
      case ApplicationConstants.FLAG_REASON:
        requestBody.flagReason.push(item.value);
        break;
      case ApplicationConstants.COHORT:
        requestBody.cohort = item.value === ApplicationConstants.ANY ? undefined : item.value;
        break;
      case ApplicationConstants.REVIEW_STATUS:
        if (item.value !== ApplicationConstants.ANY) {
          requestBody.reviewStatusList.push(item.value);
        }
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_SCORE:
        requestBody.technologyAreaScoreList.push(item.value);
        break;
      case ApplicationConstants.GRANT_ANNUAL_EXCEPTION:
        requestBody.annualExceptionList.push(item.value);
        break;
      case ApplicationConstants.AVAILABLE_CPM_LIST:
        if (item.value !== ApplicationConstants.ANY) {
          requestBody.availableCpmList.push(item.value);
        } break;
      default:
        return;
    }
  });

  return requestBody;
};

/**
 * Load an individual applications
 * @param {ILoadApplication} action for loading an application
 * @returns {void}
 */
function* handleLoadApplication(action: ILoadApplication) {
  const failedMessage = "Failed to load the selected application.";
  const { selectedApplicationEmail } = action;

  try {
    const { result, timeout }: IApiResult2<IApplicationDataResult> | any = yield race({
      result: call(applicationApi.getApplication, selectedApplicationEmail),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(loadApplicationFail(ApplicationConstants.GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const application = result.parsedBody as IApplicationResponse;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(loadApplicationSuccess(application.applicationForm));
        break;
      case HttpStatusCode.BadRequest:
        yield put(loadApplicationFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(loadApplicationFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(loadApplicationFail(error));
  }
}

/**
 * Load the existing application assignments and information
 * @param {IGetAssignApplication} action for loading an application
 * @returns {void}
 */
function* handleLoadAssignApplication(action: IGetAssignApplication) {
  const failedMessage = "Failed to load the assignment information.";
  const { applicationFormId } = action;

  try {
    const { result, timeout }: IApiResult2<IApplicationGetAssignDataResult> | any = yield race({
      result: call(applicationApi.getAssignApplication, applicationFormId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(getAssignApplicationFail(ApplicationConstants.GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const getAssignResult = result.parsedBody as IApplicationGetAssign;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(getAssignApplicationSuccess(getAssignResult));
        break;
      case HttpStatusCode.BadRequest:
        yield put(getAssignApplicationFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(getAssignApplicationFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(getAssignApplicationFail(error.message || error || failedMessage));
  }
}

/**
 * Assigns the application to admins for technology areas.
 * @param {IAssignApplication} action for assigning an application
 * @returns {void}
 */
function* handleAssignApplication(action: IAssignApplication) {
  const failedMessage = "Failed to assign the application.";

  const requestBody: IApplicationAssign = {
    applicationFormId: action.applicationFormId,
    applicationAssignees: action.technologyAssignments,
  };

  try {
    const { result, timeout }: IApiResult2<string[]> | any = yield race({
      // TODO: Update this IApiResult2<any> to use actual ApiResult type
      result: call(applicationApi.assignApplication, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(assignApplicationFail(ApplicationConstants.GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
      case HttpStatusCode.CreatedAt:
        yield put(assignApplicationSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(assignApplicationFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(assignApplicationFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(assignApplicationFail(failedMessage));
  }
}

/**
 * Load the existing available application assignments and information
 * @param {IGetAvailableAssignApplication} action for loading an application
 * @returns {void}
 */
function* handleLoadAvailableAssignApplication(action: IGetAvailableAssignApplication) {
  const failedMessage = "Failed to load the assignment information.";
  const { applicationFormIds } = action;

  try {
    const { result, timeout }: IApiResult2<IApplicationGetBulkAssignDataResult> | any = yield race({
      result: call(applicationApi.getAvailableAssignees, applicationFormIds),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(getAvailableAssignApplicationFail(ApplicationConstants.GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const getAssignResult = result.parsedBody as IApplicationGetBulkAssign;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(getAvailableAssignApplicationSuccess(getAssignResult));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          getAvailableAssignApplicationFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      case HttpStatusCode.ServerError:
        yield put(
          getAvailableAssignApplicationFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(getAvailableAssignApplicationFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(getAvailableAssignApplicationFail(error.message || error || failedMessage));
  }
}

/**
 * Assigns the bulk application to admins for technology areas.
 * @param {IBulkAssignApplication} action for assigning an application
 * @returns {void}
 */
function* handleBulkAssignApplication(action: IBulkAssignApplication) {
  const failedMessage = "Failed to assign the application.";

  const requestBody: IApplicationBulkAssign = {
    applicationFormIds: action.applicationFormIds,
    applicationAssignees: action.technologyAssignments,
  };

  try {
    const { result, timeout }: IApiResult2<string[]> | any = yield race({
      // TODO: Update this IApiResult2<any> to use actual ApiResult type
      result: call(applicationApi.bulkAssignApplications, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(bulkAssignApplicationFail(ApplicationConstants.GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
      case HttpStatusCode.CreatedAt:
        yield put(bulkAssignApplicationSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(bulkAssignApplicationFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(bulkAssignApplicationFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(bulkAssignApplicationFail(failedMessage));
  }
}

/**
 * Scores the application to admins for a technology area.
 * @param {IScoreApplication} action for assigning an application
 * @returns {void}
 */
function* handleScoreApplication(action: IScoreApplication) {
  const failedMessage = "Failed to score the application.";

  const request: IApplicationScoreRequest = {
    applicationFormId: action.applicationFormId,
    technologyFocusArea: action.technologyFocusArea,
    score: action.score,
    justification: action.justification,
  };

  try {
    const { result, timeout }: IApiResult2<boolean> | any = yield race({
      result: call(applicationApi.scoreApplication, request),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(scoreApplicationFail(ApplicationConstants.GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
      case HttpStatusCode.CreatedAt:
        yield put(scoreApplicationSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(scoreApplicationFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(scoreApplicationFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(scoreApplicationFail(failedMessage));
  }
}

/**
 * Flags the application for further review.
 * @param {IFlagApplication} action for flagging an application
 * @returns {void}
 */
function* handleFlagApplication(action: IFlagApplication) {
  const failedMessage = "Failed to flag the application.";

  const request: IApplicationFlagRequest = {
    applicationFormId: action.applicationFormId,
    justification: action.justification,
    flagReasonId: action.flagReasonId,
  };

  try {
    const { result, timeout }: IApiResult2<boolean> | any = yield race({
      result: call(applicationApi.flagApplication, request),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(flagApplicationFail(ApplicationConstants.GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
      case HttpStatusCode.CreatedAt:
        yield put(flagApplicationSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(flagApplicationFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(flagApplicationFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(flagApplicationFail(failedMessage));
  }
}

/**
 * Search all applications for given text
 * @param {ISearchApplications} action for searching applications
 * @returns {void}
 */
function* handleSearchApplications(action: ISearchApplications) {
  const failedMessage = "Failed to search the applications.";
  const requestBody = {
    applicationStatusList: [],
    countryRegionList: [],
    pageIndex: action.searchPage,
    pageSize: action.pageSize,
    searchKey: action.searchText,
    stateProvinceList: [],
    annualExceptionList: [],
    applicationType: action.applicationType,
    requestorEmailAddress: action.requestorEmailAddress,
    technologyAreaList: [],
    technologyAreaGroupList: [],
    assignedToMe: undefined,
    technologyAreaScoreList: [],
    technologyAreaStatusList: [],
    technologyAreaStatusReasonList: [],
    flagReason: [],
    cohort: undefined,
    reviewStatusList: [],
    availableCpmList: [],
    sortColumn: action.sortColumn,
    sortDescending: action.sortDescending,
  } as unknown as IApplicationsRequest;

  action.selectedApplicationsFilterValues.forEach(function (item) {
    switch (item.fieldName) {
      case ApplicationConstants.APPLICATION_STATUS:
        requestBody.applicationStatusList.push(item.value);
        break;
      case ApplicationConstants.APPLICATION_COUNTRY_REGION:
        requestBody.countryRegionList.push(item.value);
        break;
      case ApplicationConstants.APPLICATION_STATE_PROVINCE:
        requestBody.stateProvinceList.push(item.value);
        break;
      case ApplicationConstants.GRANT_ANNUAL_EXCEPTION:
        requestBody.annualExceptionList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_GROUP:
        requestBody.technologyAreaGroupList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA:
        requestBody.technologyAreaList.push(item.value);
        break;
      case ApplicationConstants.ASSIGNED_TO:
        if (item.value !== ApplicationConstants.ANY) {
          requestBody.assignedToMe = item.value;
        }
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_SCORE:
        requestBody.technologyAreaScoreList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_STATUS:
        requestBody.technologyAreaStatusList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_STATUS_REASON:
        requestBody.technologyAreaStatusReasonList.push(item.value);
        break;
      case ApplicationConstants.FLAG_REASON:
        requestBody.flagReason.push(item.value);
        break;
      case ApplicationConstants.COHORT:
        if (item.value !== ApplicationConstants.ANY) {
          requestBody.cohort = item.value;
        }
        break;
      case ApplicationConstants.REVIEW_STATUS:
        if (item.value !== ApplicationConstants.ANY) {
          requestBody.reviewStatusList.push(item.value);
        }
        break;
      case ApplicationConstants.AVAILABLE_CPM_LIST:
        if (item.value !== ApplicationConstants.ANY) {
          requestBody.availableCpmList.push(item.value);
        }
      default:
        return;
    }
  });
  try {
    const { result, timeout }: IApiResult2<IApplicationListDataResult> | any = yield race({
      result: call(applicationApi.getApplications, requestBody, action.tenant.toLowerCase()),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(searchApplicationsFail(ApplicationConstants.GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const parsedBody = result.parsedBody;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          searchApplicationsSuccess(
            parsedBody.applicationForms,
            action.searchPage,
            action.pageSize,
            parsedBody.filteredCount
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(searchApplicationsFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(searchApplicationsFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(searchApplicationsFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IRDDownloadToCSV} action To download user profiles.
 * @returns {void}
 */
function* handleDownloadApplicationsToCSV(action: IDownloadApplicationsToCSV) {
  const defaultFailedErrorMessage = "Failed to download applications";
  const requestBody = updateFilterValues(action);
  requestBody.searchKey = action.searchText === undefined ? "" : action.searchText;

  try {
    const { result, timeout } = yield race({
      result: call(applicationApi.downloadApplicationsToCSV, requestBody, action.tenant),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(downloadApplicationsToCSVFail(ApplicationConstants.GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const data = yield result.text();

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(downloadApplicationsToCSVSuccess(data));
        break;
      case HttpStatusCode.BadRequest:
        yield put(downloadApplicationsToCSVFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(downloadApplicationsToCSVFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(downloadApplicationsToCSVFail(defaultFailedErrorMessage));
  }
}

export {
  handleDownloadApplicationsToCSV,
  handleFlagApplication,
  handleLoadApplication,
  handleSearchApplications,
  handleLoadAssignApplication,
  handleAssignApplication,
  handleScoreApplication,
  handleLoadAvailableAssignApplication,
  handleBulkAssignApplication,
};
